import { isServer, keepPreviousData, QueryClient } from '@tanstack/react-query';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { routeTree } from './routeTree.gen';
import {
	persistQueryClient,
	PersistQueryClientProvider,
	removeOldestQuery,
} from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { PERSISTED_QUERY_CACHE_KEY } from './lib/query-persistence';
import type { ReactNode } from 'react';
import { gtag } from '@worldcoo/ui/lib/analytics';
import { getRuntimeAnalyticsCode } from './lib/shared';
import { loadLandingTranslation } from './lib/i18n';
import { extractErrorInfo, getRequestContext, logErrorFn } from './lib/data';
import { portalLocales, type PortalLocale } from '@worldcoo/monolith/portals';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import {
	DefaultCatchBoundary,
	VERSION_SKEW_RELOAD_COOLDOWN_MS,
	VERSION_SKEW_RELOAD_KEY,
} from './routes/-components/default-catch-boundary';
import { NotFound } from './routes/-components/not-found';
import { portalSlugs, wwwSlugs } from './lib/slug-rewrites';
import { detectLocale } from '@worldcoo/monolith/core';

/**
 * During a version skew reload, the DOM is being torn down while React streaming SSR
 * scripts still execute, causing benign errors (e.g., "Cannot read properties of null
 * (reading 'parentNode')"). Suppress logging these to the backend.
 */
function isVersionSkewReloadInProgress() {
	const lastReload = window.sessionStorage.getItem(VERSION_SKEW_RELOAD_KEY);
	return !!lastReload && Date.now() - parseInt(lastReload) < VERSION_SKEW_RELOAD_COOLDOWN_MS;
}

/**
 * Mutable locale state shared between the rewrite closures and the rest of the app.
 * On the server, each `getRouter()` call creates a new one (request-scoped).
 * On the client, there's a single instance for the session lifetime.
 *
 * `hasLocalePrefix` tracks whether the incoming URL had an explicit locale segment.
 * When false (pathless URL), the output rewrite omits the prefix so the URL stays
 * as-is and content is served with the auto-detected language.
 */
let _localeState: { locale: string; hasLocalePrefix: boolean } | undefined;

/** Update the current locale used by the rewrite output (called from language switching). */
export function updateRouterLocale(locale: string) {
	if (_localeState) {
		_localeState.locale = locale;
		_localeState.hasLocalePrefix = true;
	}
}

/**
 * Whether the URL currently being rendered has an explicit locale segment (`/es/...`)
 * versus the unprefixed root (`/...`). Used by SEO components to keep the canonical URL
 * pointing at the page itself instead of at the prefixed hreflang variant — Lighthouse
 * (and Google) flag canonicals that match a non-self hreflang alternate as invalid.
 */
export function hasLocalePrefix(): boolean {
	return _localeState?.hasLocalePrefix ?? true;
}

function isPortalLocale(segment: string | undefined): segment is string {
	return !!segment && (portalLocales as readonly string[]).includes(segment);
}

/**
 * TODO: We're using the tanstack start built-in RPC instead of trpc because trpc
 * needs to send the proper headers to the backend and this disables tanstack query persist
 * We lose the batching from TRPC but we gain the ability to persist queries
 */
export async function getRouter() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 60 * 1000,
				gcTime: Infinity,
				placeholderData: keepPreviousData,
			},
		},
	});

	const persister = createAsyncStoragePersister({
		key: PERSISTED_QUERY_CACHE_KEY,
		storage: typeof window !== 'undefined' ? window.localStorage : undefined,
		retry: removeOldestQuery,
	});

	const buster = 'v4';

	// Cookies are the source of truth for auth. Persisting auth queries would make the
	// previous user's identity survive a page reload until the background refetch lands.
	const dehydrateOptions = {
		shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string; data?: unknown } }) => {
			const first = query.queryKey[0];
			if (Array.isArray(first) && first[0] === 'auth') return false;
			if (query.state.status !== 'success') return false;
			// `{ unauthenticated: true }` is returned as successful query *data* (not an error) by both
			// transports, so it would otherwise be persisted. Restoring it on the next load makes a
			// freshly-authenticated user read a stale "logged out" result synchronously and bounce back
			// to /login before the cookie-backed refetch can land. Never persist it.
			const data = query.state.data;
			if (data && typeof data === 'object' && 'unauthenticated' in data) return false;
			return true;
		},
	};

	// TODO: We're calling it also here instead of relying only on <PersistQueryClientProvider />
	// because on quick reload is still not restoring from persister
	if (!isServer) {
		persistQueryClient({ queryClient, persister, buster, dehydrateOptions });
	}

	// Server: compute request context from headers once. Client: placeholder until hydrate().
	const serverContext = isServer ? await getRequestContext() : undefined;
	const requestContext = {
		baseUrl: serverContext?.baseUrl ?? '',
		portalId: serverContext?.portalId ?? '',
		locale: serverContext?.locale ?? 'es',
		isBot: serverContext?.isBot ?? false,
		isInIframe: serverContext?.isInIframe ?? false,
	};

	// Mutable locale state captured by rewrite closures — scoped per router instance
	const localeState: { locale: string; hasLocalePrefix: boolean } = {
		locale: requestContext.locale,
		hasLocalePrefix: true,
	};
	_localeState = localeState;

	// Shared mutable context object — the rewrite.input closure updates `locale`
	// before the first render so SSR and client agree on the locale.
	const routerContext = { queryClient, ...requestContext };

	await loadLandingTranslation(
		isServer ? requestContext.locale : (document.documentElement.lang as PortalLocale) || 'es',
	);

	const router = createTanStackRouter({
		routeTree,
		context: routerContext,
		// Dehydrate server-computed request context (including URL-detected locale)
		// so the client receives it without a server function call on every navigation.
		dehydrate: () => ({
			requestContext: {
				...requestContext,
				locale: detectLocale(localeState.locale, portalLocales),
			},
		}),
		hydrate: (dehydrated) => {
			if (dehydrated.requestContext) {
				localeState.locale = dehydrated.requestContext.locale;
				if (router.options.context) {
					Object.assign(router.options.context, dehydrated.requestContext);
				}
			}
		},
		defaultPreload: 'intent',
		scrollRestoration: true,
		rewrite: {
			input: ({ url }) => {
				// Skip rewriting for routes that must live at the root level
				if (url.pathname.startsWith('/media/')) return url;
				if (url.pathname.startsWith('/api/trpc')) return url;

				const hostParts = url.hostname.split('.');
				const isMainDomain =
					hostParts[0] === 'www' ||
					(hostParts.length < 3 && hostParts.at(-1) !== 'localhost') ||
					(hostParts.length < 2 && hostParts.at(-1) === 'localhost');

				const prefix = isMainDomain ? 'www' : hostParts[0]!;
				url.pathname = `/${prefix}${url.pathname}`;

				if (prefix !== 'ngos') {
					const segments = url.pathname.split('/').filter(Boolean);
					if (isPortalLocale(segments[1])) {
						localeState.locale = segments[1];
						localeState.hasLocalePrefix = true;
						// Keep the router context in sync so SSR renders the correct
						// locale (e.g. <html lang="en">) on the very first pass.
						const resolvedLocale = detectLocale(segments[1], portalLocales);
						routerContext.locale = resolvedLocale;
						segments.splice(1, 1);
						url.pathname = '/' + segments.join('/');
					} else {
						localeState.hasLocalePrefix = false;
					}
				}

				// 3. Rewrite legal page slugs to internal route names
				if (prefix !== 'ngos') {
					const segments = url.pathname.split('/').filter(Boolean);
					const maps = prefix === 'www' ? wwwSlugs : portalSlugs;
					const routeName = segments[1] ? maps.slugToRoute[segments[1]] : undefined;
					if (segments.length === 2 && routeName) {
						segments[1] = routeName;
						url.pathname = '/' + segments.join('/');
					}
				}

				return url;
			},
			output: ({ url }) => {
				// Skip rewriting for routes that must live at the root level
				if (url.pathname.startsWith('/media/')) return url;
				if (url.pathname.startsWith('/api/trpc')) return url;

				const parts = url.pathname.split('/').filter(Boolean);
				if (parts.length === 0) return url;

				const prefix = parts[0];

				// Don't add locale prefix for ngos routes
				if (prefix === 'ngos') {
					url.pathname = '/' + parts.slice(1).join('/') || '/';
					return url;
				}

				// Rewrite internal legal route names back to locale-specific slugs
				if (prefix !== 'ngos' && parts.length === 2 && parts[1]) {
					const maps = prefix === 'www' ? wwwSlugs : portalSlugs;
					if (maps.routeToSlugs[parts[1]]) {
						parts[1] = maps.routeToSlugs[parts[1]]![localeState.locale] ?? parts[1];
					}
				}

				// Strip subdomain prefix; add locale prefix only if the incoming URL had one
				const rest = parts.slice(1).join('/');
				url.pathname = localeState.hasLocalePrefix ? `/${localeState.locale}${rest ? '/' + rest : ''}` : `/${rest}`;
				return url;
			},
		},
		// Since we're using React Query, we don't want loader calls to ever be stale
		// This will ensure that the loader is always called when the route is preloaded or visited
		defaultPreloadStaleTime: 0,
		Wrap: (props: { children: ReactNode }) => {
			return (
				<PersistQueryClientProvider client={queryClient} persistOptions={{ persister, buster, dehydrateOptions }}>
					{props.children}
				</PersistQueryClientProvider>
			);
		},
		defaultErrorComponent: (props) => (
			<I18nProvider i18n={i18n}>
				<DefaultCatchBoundary {...props} />
			</I18nProvider>
		),
		defaultNotFoundComponent: () => <NotFound />,
	});

	setupRouterSsrQueryIntegration({
		router,
		queryClient,
		wrapQueryClient: false,
	});

	if (!isServer) {
		/**
		 * Setup google analytics
		 */
		gtag('js', new Date());
		gtag('config', getRuntimeAnalyticsCode());

		router.history.subscribe((url) => {
			gtag('event', 'page_view', { page_location: url.location.href });
		});

		/**
		 * Reload the page if it fails to load dynamic imports
		 * When a new deployment occurs, the hosting service may delete the assets from previous deployments.
		 * As a result, a user who visited your site before the new deployment might encounter an import error.
		 * This error happens because the assets running on that user's device are outdated and it tries to import
		 * the corresponding old chunk, which is deleted.
		 * This event is useful for addressing this situation. In this case, make sure to set Cache-Control: no-cache on the HTML file,
		 * otherwise the old assets will be still referenced.
		 * See: https://vite.dev/guide/build#load-error-handling
		 */
		window.addEventListener('vite:preloadError', () => {
			window.location.reload();
		});

		/**
		 * Global error handlers for errors that escape React error boundaries
		 * (async callbacks, event handlers, setTimeout, etc.)
		 */
		window.addEventListener('error', (event) => {
			if (isVersionSkewReloadInProgress()) return;
			// Cross-origin script errors (reCAPTCHA, GA, etc.) report event.error as null.
			// These are not actionable — skip logging them.
			if (event.error == null) return;
			const errorInfo = extractErrorInfo(event.error);
			logErrorFn({
				data: {
					route: window.location.pathname,
					name: '[GLOBAL] ' + errorInfo.name,
					message: errorInfo.message,
					stack: errorInfo.stack,
				},
			}).catch(() => {});
		});

		window.addEventListener('unhandledrejection', (event) => {
			if (isVersionSkewReloadInProgress()) return;
			const errorInfo = extractErrorInfo(event.reason);
			logErrorFn({
				data: {
					route: window.location.pathname,
					name: '[UNHANDLED REJECTION] ' + errorInfo.name,
					message: errorInfo.message,
					stack: errorInfo.stack,
				},
			}).catch(() => {});
		});
	}

	return router;
}
